import { cn } from '../cn';

import type { ContactSignalGridItem } from './ContactSignalsGrid';

export type BucketTooltipBodyBucket = {
  id: string;
  name: string;
  aspects: ContactSignalGridItem[];
};

export type BucketTooltipBodyProps = {
  bucket: BucketTooltipBodyBucket;
  ignoredAspectKeys?: ReadonlySet<string>;
};

export function BucketTooltipBody({ bucket, ignoredAspectKeys }: BucketTooltipBodyProps) {
  const aspectSummary = bucket.aspects
    .map((a) => a.aspect_description)
    .filter((d): d is string => Boolean(d))
    .join('\n\n');
  const summaryText = aspectSummary.length > 0 ? aspectSummary : bucket.name;
  const bucketExcluded =
    ignoredAspectKeys != null &&
    bucket.aspects.length > 0 &&
    bucket.aspects.every((a) => ignoredAspectKeys.has(a.aspect_key));

  return (
    <div className="flex flex-col gap-2 text-sm">
      <div>{summaryText}</div>
      <div className="flex flex-col gap-1">
        <div className="font-medium">Aspects</div>
        {bucket.aspects.map((a) => {
          const aspectIgnored = ignoredAspectKeys?.has(a.aspect_key) ?? false;
          const aspectTriggered = (a.value ?? 0) > 0;
          const status = aspectIgnored
            ? 'ignored'
            : aspectTriggered
              ? 'triggered'
              : a.value != null
                ? 'not triggered'
                : 'not computed';
          return (
            <div key={a.aspect_key} className={cn(aspectIgnored && 'opacity-60')}>
              <div>
                <span className={cn('font-medium', aspectIgnored && 'line-through')}>
                  {a.aspect_name}
                </span>
                <span className="ml-1 text-xs italic">({status})</span>
              </div>
              {a.aspect_description != null && a.aspect_description.length > 0 && (
                <div className="opacity-70 text-xs">{a.aspect_description}</div>
              )}
              {a.reason != null && a.reason.length > 0 && (
                <div className="-mx-2 -mb-1 mt-3 rounded-b-md bg-black/25 px-2 py-2 leading-snug">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-60">
                    Why it fired
                  </div>
                  <div className="text-sm opacity-80">{a.reason}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {bucketExcluded && (
        <div className="italic opacity-80">
          Disabled — all {bucket.aspects.length} aspect
          {bucket.aspects.length === 1 ? '' : 's'} of this signal are excluded for your team, so
          the whole signal will not influence AI steps. Re-enable it on the Signals settings page.
        </div>
      )}
    </div>
  );
}
